import {
  MrfiktivCustomFieldListElementViewModelGen,
  MrfiktivCustomFieldValueGen,
  MrfiktivProjectCustomViewFieldDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { ProjectModule } from "@/store/modules/project.store";
import { Ticket, TicketModule, TicketStatusEnum } from "@/store/modules/ticket.store";
import { VehicleModule } from "@/store/modules/vehicle.store";
import { ProjectCustomViewFieldEnum } from "@/views/project/enum/ProjectCustomViewFieldEnum";
import moment from "moment";
import { simpleDoubleDigitDate } from "../date-helper";
import VueI18n from "@/plugins/I18nPlugin";

/**
 *
 */
export class CustomProjectTableHelper {
  /**
   *
   */
  item!: Ticket | undefined;

  /**
   *
   */
  projectId: string;

  /**
   *
   */
  customConfig!: MrfiktivProjectCustomViewFieldDtoGen[];

  constructor(projectId: string, customConfig: MrfiktivProjectCustomViewFieldDtoGen[], item: Ticket | undefined) {
    this.item = item;
    this.customConfig = customConfig;
    this.projectId = projectId;
  }

  get headers(): {
    text: string;
    value: string | number | boolean;
    width?: string;
    align?: string;
    customFieldConfig?: MrfiktivCustomFieldListElementViewModelGen;
  }[] {
    const headers: {
      text: string;
      value: string | number | boolean;
      width?: string;
      align?: string;
      customFieldConfig?: MrfiktivCustomFieldListElementViewModelGen;
    }[] = [];
    if (!this.customConfig) {
      return headers;
    }
    for (const field of this.customConfig) {
      if (field.type === ProjectCustomViewFieldEnum.CUSTOM_FIELD) {
        const config = this.getCustomFieldConfig(field.key);
        if (config) {
          headers.push({
            text: config.customField.label,
            value: field.key + ".value",
            width: "200px",
            customFieldConfig: config
          });
        }
      }
      if (field.type === ProjectCustomViewFieldEnum.REF) {
        if (field.key === BackendResourceEnum.VEHICLE) {
          headers.push({
            text: "Fahrzeug",
            value: field.key + ".value",
            width: "200px"
          });
        }
      }
      if (field.type === ProjectCustomViewFieldEnum.PROPERTY) {
        if (field.key === "state") {
          headers.push({
            text: VueI18n.t("project.ticket.state").toString(),
            value: field.key,
            width: "90px",
            align: "start"
          });
        } else if (field.key === "number") {
          headers.push({
            text: VueI18n.t("project.ticket.number").toString(),
            value: field.key,
            width: "100px",
            align: "start"
          });
        } else if (field.key === "title") {
          headers.push({
            text: VueI18n.t("project.ticket.title").toString(),
            value: field.key,
            width: "300px",
            align: "start"
          });
        } else if (field.key === "assignees") {
          headers.push({
            text: VueI18n.t("project.ticket.assignees").toString(),
            value: field.key,
            width: "200px",
            align: "start"
          });
        } else if (field.key === "due") {
          headers.push({
            text: VueI18n.t("project.ticket.due").toString(),
            value: field.key,
            width: "200px",
            align: "start"
          });
        } else {
          headers.push({
            text: field.key,
            value: field.key,
            width: "200px"
          });
        }
      }
    }
    return headers;
  }

  getCustomFieldConfig(customFieldId: string) {
    return this.customFieldConfigArray?.find(c => c.customField.id === customFieldId);
  }

  simpleDoubleDigitDate(item: MrfiktivCustomFieldValueGen) {
    if (item?.value) {
      return simpleDoubleDigitDate(item.value);
    }
    return "";
  }

  getIsoWeek(item: MrfiktivCustomFieldValueGen) {
    if (item?.value) {
      return moment(new Date(item.value)).isoWeek();
    }
    return "";
  }

  /**
   * adds custom properties to item, e.g custom field with key 1234 is "open"
   * {..., 1234: "open"}
   *
   * also returns the added fields as array for the table;
   *
   * @returns
   */
  getExtendetDocument(): {
    id: string;
    label: string;
    description: string;
    value: string;
    color?: string;
    dark?: boolean;
    style?: string;
  }[] {
    if (!this.item) {
      return [];
    }
    const fields: {
      id: string;
      label: string;
      description: string;
      value: string;
      color?: string;
      dark?: boolean;
      style?: string;
    }[] = [];

    if (!this.customConfig) {
      return fields;
    }
    for (const config of this.customConfig) {
      /**
       * CustomFields lookup
       * If custom fields are configure, searches for the custom field if its set in the specific ticket
       * checks for different types of ticket
       * Transforms
       */
      if (config.type === ProjectCustomViewFieldEnum.CUSTOM_FIELD) {
        const found = this.item.values.find(field => field.id === config.key);
        if (found && found?.value) {
          const customField = this.getCustomFieldConfig(found.id);
          if (customField?.customField.type === "date") {
            const item = {
              id: found.id,
              label: this.getCustomFieldLabel(found),
              description: this.getCustomFieldLabel(found),
              value: this.simpleDoubleDigitDate(found),
              color: undefined,
              dark: true,
              style: undefined
            };
            this.item[config.key] = item;
            fields.push(item);
          } else if (customField?.customField.type === "single_select") {
            const item = {
              id: found.id,
              label: this.getCustomFieldLabel(found),
              description: this.getCustomFieldLabel(found),
              color: this.getCustomFieldColor(found)?.color,
              dark: true,
              style: this.getCustomFieldColor(found)?.style,
              value: found.value || ""
            };
            this.item[config.key] = item;
            fields.push(item);
          } else {
            const item = {
              id: found.id,
              label: this.getCustomFieldLabel(found),
              description: this.getCustomFieldLabel(found),
              dark: false,
              color: undefined,
              style: undefined,
              value: found.value || ""
            };
            if (item) {
              this.item[config.key] = item;
              fields.push(item);
            }
          }
        }
      }
      if (config.type === ProjectCustomViewFieldEnum.REF) {
        if (config.key === BackendResourceEnum.VEHICLE) {
          /**
           * Filter all refs since there are potentially more refs
           */
          const refs = this.item.refs?.filter(ref => ref.refType === BackendResourceEnum.VEHICLE);
          if (refs) {
            for (const vehicle of refs) {
              const foundVehicle = VehicleModule.vehicles.find(item => item.id === vehicle.refId);
              if (foundVehicle) {
                const item = {
                  id: foundVehicle.id,
                  label: "Fahrzeug",
                  description: "Fahrzeug",
                  dark: true,
                  color: "blue",
                  style: undefined,
                  value: foundVehicle.displayName || foundVehicle.numberplate
                };

                this.item[config.key] = item;
                fields.push(item);
              }
            }
          }
        }
      }

      /**
       * Only push attribute values to array since the extended item already hast state etc
       */
      if (config.type === ProjectCustomViewFieldEnum.PROPERTY) {
        if (config.key === "state") {
          if (this.item.state) {
            const item = {
              id: this.item.state,
              label: "Status",
              description: "Status",
              dark: true,
              color:
                TicketModule.ticketStateMap.get(this.item.state as TicketStatusEnum)?.color || "blue-grey lighten-4",
              style: undefined,
              value: VueI18n.t("project.ticketState." + this.item.state).toString()
            };

            fields.push(item);
          }
        }
      }
    }
    return fields;
  }

  getCustomFieldLabel(customField: MrfiktivCustomFieldValueGen): string {
    const config = this.getCustomFieldConfig(customField.id);
    if (!config) {
      return "";
    }
    return config.customField.label;
  }

  getCustomFieldColor(customField: MrfiktivCustomFieldValueGen): { color: string; style: string } | undefined {
    const config = this.getCustomFieldConfig(customField.id);
    if (!config) {
      return undefined;
    }

    const value = config.customField.configuration?.values.filter(value => value.value === customField.value);
    if (value && value.length) {
      return { color: value[0].color, style: "color:white" };
    }
  }

  get customFieldConfigArray() {
    if (!this.projectId) {
      return undefined;
    }

    return ProjectModule.maps.id.get(this.projectId)?.configuration.customFieldConfig;
  }
}
